import { useState, useMemo, useEffect, useRef } from 'react'
import { useServiceContext } from '../root'

export function useLocalStorage<T>(baseKey: string, initialValue: T) {
  const context = useServiceContext()

  // 1. Generate the scoped key
  const scopedKey = useMemo(() => {
    const userId = context.service.auth.getUserId()
    const tenantUuid = context.service.auth.getTenantUuid()
    return `${baseKey}_${userId}_${tenantUuid}`
  }, [baseKey, context.service.auth])

  // 2. Lazy Initialization with strict Nullish Coalescing emulation
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(scopedKey)
      if (item) {
        const parsed = JSON.parse(item)

        // Emulate the original code's fallback logic (e.g., parsed.key ?? default.key)
        if (typeof initialValue === 'object' && initialValue !== null && !Array.isArray(initialValue)) {
          const merged = { ...initialValue }
          for (const key in parsed) {
            if (parsed[key] !== undefined && parsed[key] !== null) {
              merged[key as keyof T] = parsed[key]
            }
          }
          return merged as T
        }
        return parsed
      }
      return initialValue
    } catch (error) {
      console.warn(`[useLocalStorage] Error reading localStorage key "${scopedKey}":`, error)
      return initialValue
    }
  })

  // 3. Sync to localStorage safely
  const isMounted = useRef(false)

  useEffect(() => {
    // Prevent writing to storage on the initial mount
    if (!isMounted.current) {
      isMounted.current = true
      return
    }

    try {
      window.localStorage.setItem(scopedKey, JSON.stringify(storedValue))
    } catch (error) {
      console.warn(`[useLocalStorage] Error setting localStorage key "${scopedKey}":`, error)
    }
  }, [storedValue, scopedKey])

  return [storedValue, setStoredValue] as const
}
